import React, { useState, useEffect, useMemo } from 'react';
import {
  object,
  func,
  number,
  bool,
  shape,
  instanceOf,
  string,
  array,
} from 'prop-types';
import { intlShape, injectIntl } from '../../../../util/reactIntl';
import { lazyLoadWithDimensions } from '../../../../util/uiHelpers';
import { convertPrice, formatMoney } from '../../../../util/currency';
import { createSlug } from '../../../../util/urlHelpers';
import { generateCategorySlug, generateCitySlug } from '../../../../util/seo';
import {
  AspectRatioWrapper,
  Button,
  NamedLink,
  ResponsiveImage,
} from '../../../../components';
import { useDragScroll } from '../../../../hooks/useDragScroll';
import classNames from 'classnames';

// Shopping cart icon SVG
const CartIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M7 18C5.9 18 5.01 18.9 5.01 20C5.01 21.1 5.9 22 7 22C8.1 22 9 21.1 9 20C9 18.9 8.1 18 7 18ZM1 2V4H3L6.6 11.59L5.25 14.04C5.09 14.32 5 14.65 5 15C5 16.1 5.9 17 7 17H19V15H7.42C7.28 15 7.17 14.89 7.17 14.75L7.2 14.63L8.1 13H16.55C17.3 13 17.96 12.59 18.3 11.97L21.88 5.48C21.96 5.34 22 5.17 22 5C22 4.45 21.55 4 21 4H5.21L4.27 2H1ZM17 18C15.9 18 15.01 18.9 15.01 20C15.01 21.1 15.9 22 17 22C18.1 22 19 21.1 19 20C19 18.9 18.1 18 17 18Z"
      fill="currentColor"
    />
  </svg>
);
import { types as sdkTypes } from 'sharetribe-flex-sdk';
import { sortPackages, getCustomPackagePrice } from '../../../../util/data';
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min';
import { stringify } from '../../../../util/urlHelpers';
import css from './RentalCard.module.css';

const { Money } = sdkTypes;

// QuantityInput component - separate component outside RentalCardComponent
const QuantityInput = ({
  quantity,
  onQuantityChange,
  maxQuantity,
  disabled,
  intl,
  onRequireDates,
  shouldOpenDatePickerOnDisabled,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState(quantity?.toString() || '1');
  const [showMaxMessage, setShowMaxMessage] = useState(false);
  const [showMinMessage, setShowMinMessage] = useState(false);
  const [showInvalidMessage, setShowInvalidMessage] = useState(false);

  useEffect(() => {
    if (quantity > maxQuantity) {
      setShowMaxMessage(true);
    }
  }, [quantity, maxQuantity]);

  // Update display value when quantity prop changes (but not when focused)
  useEffect(() => {
    if (!isFocused) {
      setDisplayValue(quantity?.toString() || '1');
    }
  }, [quantity, isFocused]);

  const handleDecrease = e => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) {
      if (shouldOpenDatePickerOnDisabled && onRequireDates) {
        onRequireDates();
      }
      return;
    }
    const currentValue = parseInt(quantity || 1, 10);
    if (currentValue > 1 && onQuantityChange) {
      onQuantityChange(currentValue - 1);
      setShowInvalidMessage(false);
      setShowMinMessage(false);
      setShowMaxMessage(false);
    } else {
      setShowMinMessage(true);
      setShowInvalidMessage(false);
      setShowMaxMessage(false);
    }
  };

  const handleIncrease = e => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) {
      if (shouldOpenDatePickerOnDisabled && onRequireDates) {
        onRequireDates();
      }
      return;
    }
    const currentValue = parseInt(quantity || 1, 10);
    if (currentValue < maxQuantity && onQuantityChange) {
      onQuantityChange(currentValue + 1);
      setShowInvalidMessage(false);
      setShowMinMessage(false);
      setShowMaxMessage(false);
    } else {
      setShowMaxMessage(true);
      setShowInvalidMessage(false);
      setShowMinMessage(false);
    }
  };

  const handleInputChange = e => {
    if (disabled) return;
    const value = e.target.value;
    setDisplayValue(value);
    // Only update if value is valid
    if (
      value === '' ||
      (parseInt(value, 10) >= 1 && parseInt(value, 10) <= maxQuantity)
    ) {
      if (onQuantityChange) {
        onQuantityChange(value === '' ? 1 : parseInt(value, 10));
        setShowInvalidMessage(false);
        setShowMinMessage(false);
        setShowMaxMessage(false);
      }
    } else {
      setShowInvalidMessage(true);
      setShowMinMessage(false);
      setShowMaxMessage(false);
    }
  };

  const handleInputFocus = e => {
    if (disabled) return;
    setIsFocused(true);
    setDisplayValue('');
  };

  const handleInputBlur = e => {
    if (disabled) return;
    setIsFocused(false);
    const value = e.target.value;
    if (value === '' || parseInt(value, 10) < 1) {
      const finalValue = 1;
      setDisplayValue(finalValue.toString());
      if (onQuantityChange) {
        onQuantityChange(finalValue);
      }
    } else if (parseInt(value, 10) > maxQuantity) {
      const finalValue = maxQuantity;
      setDisplayValue(finalValue.toString());
      if (onQuantityChange) {
        onQuantityChange(finalValue);
      }
    } else {
      setDisplayValue(value);
    }
  };

  const errorMessage = showMaxMessage
    ? intl.formatMessage(
      { id: 'QuantityInput.maxQuantityMessage' },
      { maxQuantity }
    )
    : showMinMessage
      ? intl.formatMessage({ id: 'QuantityInput.minQuantityMessage' })
      : showInvalidMessage
        ? intl.formatMessage(
          { id: 'QuantityInput.invalidQuantityMessage' },
          { maxQuantity }
        )
        : null;
  return (
    <div className={css.quantityControlsContainer}>
      <div className={css.quantityControls}>
        <button
          type="button"
          className={classNames(
            css.quantityButton,
            (disabled || quantity <= 1) && css.disabled
          )}
          onClick={handleDecrease}
          // disabled={disabled || quantity <= 1}
          aria-label={intl.formatMessage({ id: 'CartModal.decreaseQuantity' })}
        >
          <span className={css.quantityButtonText}>-</span>
        </button>
        <input
          type="number"
          className={css.quantityInput}
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={disabled}
          min={1}
          max={maxQuantity}
          aria-label={intl.formatMessage({ id: 'CartModal.quantityAriaLabel' })}
        />
        <button
          type="button"
          className={classNames(
            css.quantityButton,
            (disabled || quantity >= maxQuantity) && css.disabled
          )}
          onClick={handleIncrease}
          // disabled={disabled || quantity >= maxQuantity}
          aria-label={intl.formatMessage({ id: 'CartModal.increaseQuantity' })}
        >
          <span className={css.quantityButtonText}>+</span>
        </button>
      </div>
      <div className={css.errorMessage}>{errorMessage}</div>
    </div>
  );
};

QuantityInput.propTypes = {
  quantity: number.isRequired,
  onQuantityChange: func.isRequired,
  maxQuantity: number.isRequired,
  disabled: bool,
  onRequireDates: func,
  shouldOpenDatePickerOnDisabled: bool,
};

QuantityInput.defaultProps = {
  disabled: false,
  onRequireDates: null,
  shouldOpenDatePickerOnDisabled: false,
};

// Package key to display label mapping
const getPackageLabel = key => {
  switch (key) {
    case 'perDayPrice':
      return 'Per day';
    case 'twoDaysPrice':
      return '2 days';
    case 'threeDaysPrice':
      return '3 days';
    case 'fourDaysPrice':
      return '4 days';
    case 'fiveDaysPrice':
      return '5 days';
    case 'oneWeekPrice':
      return '7 days';
    case 'twentyEightDaysPrice':
      return '28 days';
    case 'perHourPrice':
      return 'Per hour';
    case 'perMinutePrice':
      return 'Per minute';
    case 'overnightPrice':
      return 'Overnight';
    default:
      return '';
  }
};

// Get selected duration from date range - calendar days counted in the store's zone
// (the filter dates are store-zone midnights), so a spring-forward DST change inside
// the range can't truncate the count a day short. Must agree with CustomHits'
// getSelectedDuration, which picks the package the item is actually added with.
const getSelectedDuration = (dateRangeFilter, storeTimeZone) => {
  if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
    return null;
  }
  const inZone = d => (storeTimeZone ? moment(d).tz(storeTimeZone) : moment(d));
  return inZone(dateRangeFilter.endDate).diff(inZone(dateRangeFilter.startDate), 'days');
};

// Get pricing tiers for display
const getPricingTiers = (
  prices,
  exchangeRates,
  currentUserCurrency,
  intl,
  defaultPriceKey
) => {
  if (
    !prices ||
    typeof prices !== 'object' ||
    !exchangeRates ||
    exchangeRates.length === 0
  ) {
    return [];
  }

  const sortedPrices = sortPackages(prices, defaultPriceKey || 'perDayPrice');

  // Order for display: perDayPrice, twoDaysPrice, threeDaysPrice, fourDaysPrice, fiveDaysPrice, oneWeekPrice, twentyEightDaysPrice
  const priceOrder = [
    'perDayPrice',
    'twoDaysPrice',
    'threeDaysPrice',
    'fourDaysPrice',
    'fiveDaysPrice',
    'oneWeekPrice',
    'twentyEightDaysPrice',
  ];

  return priceOrder
    .filter(key => sortedPrices[key] != null)
    .map(key => {
      const priceObj = sortedPrices[key];
      const convertedPrice = convertPrice(
        exchangeRates,
        priceObj.amount,
        priceObj.currency,
        currentUserCurrency
      );

      if (!convertedPrice) return null;

      return {
        key,
        label: getPackageLabel(key),
        price: convertedPrice,
        formattedPrice: formatMoney(
          intl,
          new Money(convertedPrice.amount, convertedPrice.currency)
        ),
      };
    })
    .filter(tier => tier !== null);
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, {
  loadAfterInitialRendering: 3000,
});

function truncateText(text, maxLength) {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength) + '...';
}

export const RentalCardComponent = props => {
  // Drag-to-scroll for pricing tiers container
  const pricingTiersRef = React.useRef(null);
  useDragScroll(pricingTiersRef, css);

  const {
    rental = {},
    intl,
    config,
    exchangeRates,
    currentUserCurrency,
    isCurrentUser,
    dateRangeFilter,
    storeTimeZone,
    quantity = 1,
    onQuantityChange,
    onAddToCart,
    availabilityStatus,
    availabilityLoading,
    addToCartInProgress,
    isAdded = false,
    isInCart = false,
    cartItem = null,
    cartQuantity = 1,
    isLoading = false,
    isAdding = false,
    isUpdating = false,
    viewMode = 'grid',
    dateRangePickerRef,
    datePickerAnchorRef,
    storePath,
  } = props;

  const id = rental.objectID;
  const { title = '', price, publicData } = rental;

  const {
    category,
    city,
    listingType,
    defaultPriceKey,
    transactionProcessAlias,
    listingImages = [],
    prices = {},
    quantity: maxQuantity = 10, // Default max quantity
  } = publicData || {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const slug = createSlug(title);
  const citySlug = generateCitySlug(city);
  const categorySlug = generateCategorySlug(category);

  // Get pricing tiers
  const pricingTiers = getPricingTiers(
    prices,
    exchangeRates,
    currentUserCurrency,
    intl,
    defaultPriceKey
  );

  // Get selected duration from date range
  const selectedDuration = getSelectedDuration(dateRangeFilter, storeTimeZone);

  // Determine which tier is selected based on duration
  const getSelectedTierKey = () => {
    if (!selectedDuration) return null;
    const durationMap = {
      1: 'perDayPrice',
      2: 'twoDaysPrice',
      3: 'threeDaysPrice',
      4: 'fourDaysPrice',
      5: 'fiveDaysPrice',
      7: 'oneWeekPrice',
      28: 'twentyEightDaysPrice',
    };
    return durationMap[selectedDuration] || null;
  };
  const selectedTierKey = getSelectedTierKey();

  // Check if selected duration matches an existing package
  const hasMatchingPackage = useMemo(() => {
    return (
      selectedTierKey && pricingTiers.some(tier => tier.key === selectedTierKey)
    );
  }, [selectedTierKey, pricingTiers]);

  // Calculate custom package price if duration doesn't match existing packages
  const customPackage = useMemo(() => {
    if (
      !selectedDuration ||
      hasMatchingPackage ||
      !prices ||
      Object.keys(prices).length === 0
    ) {
      return null;
    }

    const customPrice = getCustomPackagePrice(selectedDuration, prices);
    if (!customPrice) return null;

    const convertedPrice = convertPrice(
      exchangeRates,
      customPrice.amount,
      customPrice.currency,
      currentUserCurrency
    );

    if (!convertedPrice) return null;

    return {
      key: 'custom',
      label: `${selectedDuration} ${selectedDuration === 1 ? 'day' : 'days'}`,
      price: convertedPrice,
      formattedPrice: formatMoney(
        intl,
        new Money(convertedPrice.amount, convertedPrice.currency)
      ),
      isCustom: true,
    };
  }, [
    selectedDuration,
    hasMatchingPackage,
    prices,
    exchangeRates,
    currentUserCurrency,
    intl,
  ]);

  // Combine existing tiers with custom package if needed
  const displayTiers = useMemo(() => {
    if (customPackage) {
      // Insert custom package in appropriate position (sorted by duration)
      const tiersWithCustom = [...pricingTiers];
      // Sort by duration (approximate based on key)
      const durationOrder = {
        perDayPrice: 1,
        twoDaysPrice: 2,
        threeDaysPrice: 3,
        fourDaysPrice: 4,
        fiveDaysPrice: 5,
        oneWeekPrice: 7,
        twentyEightDaysPrice: 28,
      };
      const sortedTiers = tiersWithCustom.sort((a, b) => {
        const aDuration = a.isCustom
          ? selectedDuration
          : durationOrder[a.key] || 999;
        const bDuration = b.isCustom
          ? selectedDuration
          : durationOrder[b.key] || 999;
        return aDuration - bDuration;
      });

      return [customPackage, ...sortedTiers];
    }
    return pricingTiers;
  }, [pricingTiers, customPackage, selectedDuration]);

  // Check if dates are selected
  const hasDates = !!(dateRangeFilter?.startDate && dateRangeFilter?.endDate);

  // Check if listing is available
  const isAvailable = availabilityStatus === true;
  const isLoadingAvailability = availabilityLoading === true;

  // Determine if Add to Cart button should be enabled
  // If item is in cart, button should show "Added to cart" but still allow quantity updates
  const canAddToCart =
    hasDates &&
    isAvailable &&
    !isLoadingAvailability &&
    !addToCartInProgress &&
    !isAdded &&
    !isInCart &&
    !isLoading;

  // Quantity input is disabled if dates are empty or listing is not available
  // But allow quantity updates if item is in cart (as long as dates are selected and available)
  // Disable during loading to prevent multiple updates
  const isQuantityDisabled =
    !hasDates || !isAvailable || isLoadingAvailability || isLoading;

  // Quantity change handler
  const handleQuantityChange = newQuantity => {
    if (onQuantityChange) {
      onQuantityChange(id, newQuantity);
    }
  };

  const openDatePickerWithoutLosingScroll = () => {
    if (!dateRangePickerRef?.current?.focus) return;

    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      const anchorEl = datePickerAnchorRef?.current;
      if (anchorEl?.scrollIntoView) {
        anchorEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      setTimeout(() => {
        dateRangePickerRef.current?.focus?.();
      }, 220);
      return;
    }

    const scrollY = window.scrollY;
    dateRangePickerRef.current.focus();
    requestAnimationFrame(() => {
      window.scrollTo({ top: scrollY, left: 0, behavior: 'instant' });
    });
  };

  // Add to cart handler
  const handleAddToCart = e => {
    console.log('handleAddToCart');
    e.preventDefault();
    e.stopPropagation();

    console.log('canAddToCart', canAddToCart);
    console.log('onAddToCart', onAddToCart);
    if (!canAddToCart || !onAddToCart) {
      console.log('hasDates', hasDates);
      if (!hasDates) {
        console.log('openDatePickerWithoutLosingScroll');
        openDatePickerWithoutLosingScroll();
      }
      return;
    }

    onAddToCart();
  };

  const queryParams = {
    ...(city && { city: city.toLowerCase() }),
    ...(category && { category: category.toLowerCase() }),
    from: 'storefront',
    searchUrl: storePath || '',
    // Calendar days as the shopper sees them on this page (the store's zone), not the
    // browser's - the filter dates are store-zone midnights.
    ...(dateRangeFilter?.startDate && {
      startDate: (storeTimeZone ? moment(dateRangeFilter.startDate).tz(storeTimeZone) : moment(dateRangeFilter.startDate)).format('YYYY-MM-DD'),
    }),
    ...(dateRangeFilter?.endDate && {
      endDate: (storeTimeZone ? moment(dateRangeFilter.endDate).tz(storeTimeZone) : moment(dateRangeFilter.endDate)).format('YYYY-MM-DD'),
    }),
  };
  const listingSearch = stringify(queryParams);
  const linkTarget = { search: listingSearch ? `?${listingSearch}` : '' };

  const firstImage = listingImages[0];
  const firstImageMaybe =
    !!firstImage && firstImage.secure_url ? (
      <NamedLink
        name="ListingPage"
        params={{
          ...(citySlug ? { city: citySlug } : {}),
          ...(categorySlug ? { category: categorySlug } : {}),
          id,
          slug
        }}
        to={linkTarget}
      >
        <ResponsiveImage
          className={css.imageWrapper}
          alt={title || intl.formatMessage({ id: 'RentalCard.listingImageAlt' })}
          renderCloudinaryImage={{
            secure_url: firstImage.secure_url,
            alt: title,
            variantsToRender: ['listing-card'],
            defaultVariant: 'listing-card',
          }}
        />
      </NamedLink>
    ) : (
      <NamedLink
        name="ListingPage"
        params={{
          ...(citySlug ? { city: citySlug } : {}),
          ...(categorySlug ? { category: categorySlug } : {}),
          id,
          slug
        }}
        to={linkTarget}
      >
        <div className={css.noImageWrapper}>
          <svg
            color="#333"
            width="100px"
            height="100px"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M7.828 5l-1-1H22v15.172l-1-1v-.69l-3.116-3.117-.395.296-.714-.714.854-.64a.503.503 0 0 1 .657.046L21 16.067V5zM3 20v-.519l2.947-2.947a1.506 1.506 0 0 0 .677.163 1.403 1.403 0 0 0 .997-.415l2.916-2.916-.706-.707-2.916 2.916a.474.474 0 0 1-.678-.048.503.503 0 0 0-.704.007L3 18.067V5.828l-1-1V21h16.172l-1-1zM17 8.5A1.5 1.5 0 1 1 15.5 7 1.5 1.5 0 0 1 17 8.5zm-1 0a.5.5 0 1 0-.5.5.5.5 0 0 0 .5-.5zm5.646 13.854l.707-.707-20-20-.707.707z" />
            <path fill="none" d="M0 0h24v24H0z" />
          </svg>
        </div>
      </NamedLink>
    );

  const cardClassName = `${css.rentalCard} ${viewMode === 'list' ? css.rentalCardList : css.rentalCardGrid
    }`;

  return (
    <div
      className={cardClassName}
      id={id}
      onContextMenu={e => e.preventDefault()}
      onCopy={() => false}
    >
      <AspectRatioWrapper
        className={classNames(css.aspectRatioWrapper, 'aspect-ratio-wrapper')}
        width={aspectWidth}
        height={aspectHeight}
      >
        {firstImageMaybe}
      </AspectRatioWrapper>

      <div className={css.info}>
        {displayTiers.length > 0 && (
          <div
            className={classNames(
              css.pricingTiers,
              'swiper-no-swiping pricing-tiers',
              hasDates && css.pricingTiersDatesSelected
            )}
            ref={pricingTiersRef}
            onClick={e => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            {displayTiers.map(tier => (
              <div
                key={tier.key}
                className={`${css.pricingTier} ${selectedTierKey === tier.key ||
                  (tier.isCustom && selectedDuration)
                  ? css.pricingTierSelected
                  : ''
                  }`}
              >
                <div className={css.pricingTierPrice}>
                  {tier.formattedPrice}
                </div>
                <div className={css.pricingTierLabel}>{tier.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className={css.title}>
          <NamedLink
            name="ListingPage"
            params={{
              ...(citySlug ? { city: citySlug } : {}),
              ...(categorySlug ? { category: categorySlug } : {}),
              id,
              slug
            }}
            to={linkTarget}
          >
            {title}
          </NamedLink>
        </div>
        <div className={css.buttonsWrapper}>
          {/* Quantity Controls */}
          <QuantityInput
            quantity={parseInt(quantity, 10)}
            onQuantityChange={handleQuantityChange}
            maxQuantity={parseInt(maxQuantity, 10)}
            disabled={isQuantityDisabled}
            intl={intl}
            onRequireDates={openDatePickerWithoutLosingScroll}
            shouldOpenDatePickerOnDisabled={!hasDates}
          />

          <Button
            type="button"
            className={`${css.addToCartButton} ${isInCart || isAdded
              ? css.addToCartButtonAdded
              : isLoadingAvailability || canAddToCart
                ? css.addToCartButtonEnabled
                : css.addToCartButtonDisabled
              }`}
            onClick={handleAddToCart}
            inProgress={isLoading || isLoadingAvailability}
            variant={hasDates ? 'primary' : 'danger'}
            size={'sm'}
            icon={<i className="ri-shopping-cart-line" />}
            arrow={'left'}
          >
            <span className={css.addToCartText}>
              {isLoading && isAdding
                ? intl.formatMessage({ id: 'RentalCard.adding', defaultMessage: 'Adding...' })
                : isLoading && isUpdating
                ? intl.formatMessage({ id: 'RentalCard.updating', defaultMessage: 'Updating...' })
                : isInCart
                ? intl.formatMessage({ id: 'RentalCard.addedToCart', defaultMessage: 'Added to cart' })
                : isAdded
                ? intl.formatMessage({ id: 'RentalCard.itemAdded', defaultMessage: 'Item Added' })
                : isLoadingAvailability
                ? intl.formatMessage({ id: 'RentalCard.checkingAvailability', defaultMessage: 'Checking...' })
                : hasDates && availabilityStatus === false
                ? intl.formatMessage({ id: 'RentalCard.notAvailable', defaultMessage: 'Not available' })
                : hasDates
                ? intl.formatMessage({ id: 'RentalCard.addToCart', defaultMessage: 'Add To Cart' })
                : intl.formatMessage({ id: 'RentalCard.selectDatesFirst', defaultMessage: 'Select dates first' })}
            </span>
          </Button>
        </div>
      </div>
      <div className={css.buttonsWrapperMobile}>
        {/* Quantity Controls */}
        <QuantityInput
          quantity={parseInt(quantity, 10)}
          onQuantityChange={handleQuantityChange}
          maxQuantity={parseInt(maxQuantity, 10)}
          disabled={isQuantityDisabled}
          intl={intl}
          onRequireDates={openDatePickerWithoutLosingScroll}
          shouldOpenDatePickerOnDisabled={!hasDates}
        />

        <Button
          type="button"
          className={`${css.addToCartButton} ${isInCart || isAdded
            ? css.addToCartButtonAdded
            : isLoadingAvailability || canAddToCart
              ? css.addToCartButtonEnabled
              : css.addToCartButtonDisabled
            }`}
          onClick={handleAddToCart}
          inProgress={isLoading || isLoadingAvailability}
          variant={hasDates ? 'primary' : 'danger'}
          size={'sm'}
          icon={<i className="ri-shopping-cart-line" />}
          arrow={'left'}
        >
          <span className={css.addToCartText}>
            {isLoading && isAdding
              ? intl.formatMessage({ id: 'RentalCard.adding', defaultMessage: 'Adding...' })
              : isLoading && isUpdating
              ? intl.formatMessage({ id: 'RentalCard.updating', defaultMessage: 'Updating...' })
              : isInCart
              ? intl.formatMessage({ id: 'RentalCard.addedToCart', defaultMessage: 'Added to cart' })
              : isAdded
              ? intl.formatMessage({ id: 'RentalCard.itemAdded', defaultMessage: 'Item Added' })
              : isLoadingAvailability
              ? intl.formatMessage({ id: 'RentalCard.checkingAvailability', defaultMessage: 'Checking...' })
              : hasDates && availabilityStatus === false
              ? intl.formatMessage({ id: 'RentalCard.notAvailable', defaultMessage: 'Not available' })
              : hasDates
              ? intl.formatMessage({ id: 'RentalCard.addToCart', defaultMessage: 'Add To Cart' })
              : intl.formatMessage({ id: 'RentalCard.selectDatesFirst', defaultMessage: 'Select dates first' })}
          </span>
        </Button>
      </div>
    </div>
  );
};

RentalCardComponent.propTypes = {
  intl: intlShape.isRequired,
  rental: object.isRequired,
  config: object.isRequired,
  exchangeRates: array,
  currentUserCurrency: string.isRequired,
  isCurrentUser: bool,
  dateRangeFilter: shape({
    startDate: instanceOf(Date),
    endDate: instanceOf(Date),
  }),
  quantity: number,
  onQuantityChange: func,
  onAddToCart: func,
  availabilityStatus: bool,
  availabilityLoading: bool,
  addToCartInProgress: bool,
  isAdded: bool,
  isInCart: bool,
  cartItem: object,
  cartQuantity: number,
  isLoading: bool,
  isAdding: bool,
  isUpdating: bool,
  viewMode: string,
  dateRangePickerRef: object,
  datePickerAnchorRef: object,
  storePath: string,
};

export default injectIntl(RentalCardComponent);
